// TranscriptQueue.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./TranscriptQueue.scss";

const MOM_API = "http://localhost:5000/api/mom";
const GENERATE_MOM_API = "http://localhost:6000/api/mom/generate";
const TRANSCRIPT_PULL_API =
  "https://meetinghub.insync-ai-suite.com/api/v1/meetings";
const REGENERATE_MOM_API =
  "https://meetinghub.insync-ai-suite.com/api/v1/meetings";

function TranscriptQueue() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingStates, setProcessingStates] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(MOM_API);
      if (response.data.success) {
        setMeetings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching meetings:", error);
      showNotification("Failed to fetch meetings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMOM = async (eventId, meetingId) => {
    setProcessingStates((prev) => ({ ...prev, [meetingId]: "generating" }));
    try {
      await axios.post(GENERATE_MOM_API, { eventId });
      showNotification("MOM generation started successfully!", "success");
      // Refresh the list after a delay
      setTimeout(() => {
        fetchMeetings();
        setProcessingStates((prev) => ({ ...prev, [meetingId]: null }));
      }, 2000);
    } catch (error) {
      console.error("Error generating MOM:", error);
      showNotification("Failed to generate MOM", "error");
      setProcessingStates((prev) => ({ ...prev, [meetingId]: null }));
    }
  };

  const handlePullTranscript = async (meetingId) => {
    setProcessingStates((prev) => ({ ...prev, [meetingId]: "pulling" }));
    try {
      const response = await axios.post(
        `${TRANSCRIPT_PULL_API}/${meetingId}/transcript/pull`,
      );
      if (response.data.ok) {
        showNotification(response.data.message, "success");
        setTimeout(() => {
          fetchMeetings();
          setProcessingStates((prev) => ({ ...prev, [meetingId]: null }));
        }, 2000);
      }
    } catch (error) {
      console.error("Error pulling transcript:", error);
      showNotification("Failed to pull transcript", "error");
      setProcessingStates((prev) => ({ ...prev, [meetingId]: null }));
    }
  };

  const handleRegenerateMOM = async (meetingId) => {
    setProcessingStates((prev) => ({ ...prev, [meetingId]: "regenerating" }));
    try {
      const response = await axios.post(
        `${REGENERATE_MOM_API}/${meetingId}/mom/regenerate`,
      );
      if (response.data.ok) {
        showNotification("MOM regenerated successfully!", "success");
        setTimeout(() => {
          fetchMeetings();
          setProcessingStates((prev) => ({ ...prev, [meetingId]: null }));
        }, 2000);
      }
    } catch (error) {
      console.error("Error regenerating MOM:", error);
      showNotification("Failed to regenerate MOM", "error");
      setProcessingStates((prev) => ({ ...prev, [meetingId]: null }));
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  if (loading) {
    return (
      <div className="transcript-queue-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading meetings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transcript-queue-container">
      {notification.show && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="queue-header">
        <h1 className="queue-title">Meeting Minutes Queue</h1>
        <div className="queue-stats">
          Total Meetings:{" "}
          <span className="stats-number">{meetings.length}</span>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="meetings-queue-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Meeting ID</th>
              <th>Summary</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {meetings.map((meeting) => (
              <tr key={meeting._id}>
                <td className="subject-cell">
                  <div className="subject-text">
                    {meeting.subject || "Untitled Meeting"}
                  </div>
                </td>
                <td className="meeting-id-cell">
                  <div className="meeting-id-text" title={meeting.meetingId}>
                    {meeting.meetingId?.substring(0, 30)}...
                  </div>
                </td>
                <td className="summary-cell">
                  <div className="summary-preview">
                    {meeting.summary?.substring(0, 100) ||
                      "No summary available"}
                    {meeting.summary?.length > 100 && "..."}
                  </div>
                </td>
                <td className="actions-cell">
                  <div className="action-buttons">
                    <button
                      className="btn-action btn-pull"
                      onClick={() => handlePullTranscript(meeting._id)}
                      disabled={processingStates[meeting._id]}
                    >
                      {processingStates[meeting._id] === "pulling" ? (
                        <>
                          <span className="spinner-small"></span>
                          Pulling...
                        </>
                      ) : (
                        "Pull Transcript"
                      )}
                    </button>

                    <button
                      className="btn-action btn-generate"
                      onClick={() =>
                        handleGenerateMOM(meeting.eventId, meeting._id)
                      }
                      disabled={processingStates[meeting._id]}
                    >
                      {processingStates[meeting._id] === "generating" ? (
                        <>
                          <span className="spinner-small"></span>
                          Generating...
                        </>
                      ) : (
                        "Generate MOM"
                      )}
                    </button>

                    <button
                      className="btn-action btn-regenerate"
                      onClick={() => handleRegenerateMOM(meeting._id)}
                      disabled={processingStates[meeting._id]}
                    >
                      {processingStates[meeting._id] === "regenerating" ? (
                        <>
                          <span className="spinner-small"></span>
                          Regenerating...
                        </>
                      ) : (
                        "Regenerate MOM"
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TranscriptQueue;
